/**
 * CompareGamificationProvider — `?gamification=off`, default ON.
 *
 * WHAT IT GATES. The small celebration that fires when a duplicate-LinkedIn
 * re-check comes back `cleared` (fu5 sub-task 02, Part B).
 *
 * THE DEFAULT FLIPPED ON 2026-09-15, and the precondition it was waiting on is
 * the reason to say so here rather than delete the history. The original
 * instruction (2026-09-10) was "build it, demo it, do not ship it on", because
 * Trello #790 records that this class of thing "lands well or feels
 * condescending depending entirely on execution" and wanted Jared's eyes first.
 * That review happened and it ships on; the flag stays as the opt-out.
 *
 * THE FLAG IS ALSO WHAT LET THE REST OF THE SUB-TASK MERGE without waiting on
 * that review — it existed instead of a `TODO`, and the verification behaviour
 * (the re-check itself, the
 * shake, the toast) ships whether or not the flag is ever turned on.
 *
 * THIS IS A SEPARATE MECHANISM FROM `?preview=N`, ON PURPOSE. DO NOT MERGE THEM.
 * `PreviewProvider`'s param is a *cumulative delivery-phase ladder over page
 * ids* (`PHASE_PAGE_IDS` in `useFeatureAccess.ts`, topped by `MAX_PHASE`), and
 * `config/featureFlags.ts` gates whole SECTIONS AND PAGES per stage. Neither is
 * shaped for a sub-feature toggle inside one modal: bumping `MAX_PHASE` for a
 * confetti burst would put an animation into the same vocabulary as "My Imports
 * is not released yet", and every later reader of that ladder would have to
 * work out which rungs are features and which are decorations. So this is a
 * small independent provider with its own key.
 *
 * The MECHANICS are copied from `PreviewProvider` verbatim, because each of the
 * six was paid for:
 *   1. the param is read ONCE ON MOUNT, in a lazy `useState` initialiser, from
 *      `new URLSearchParams(window.location.search)`;
 *   2. the value persists to `sessionStorage` under a NAMESPACED key, so a deep
 *      link activates the mode and it survives internal navigation — which does
 *      not re-pass the param;
 *   3. parsing is EXACT-STRING AND CASE-SENSITIVE, and anything unrecognised is
 *      treated as absent and DOES NOT TOUCH sessionStorage: a typo must not
 *      clear a mode a returning tab already had;
 *   4. `URLSearchParams.get` returns the FIRST value on a repeated param. That
 *      is the platform default; `PreviewProvider` documents it explicitly
 *      because an earlier brief asserted last-wins and was wrong;
 *   5. the context exposes the value, a derived boolean, and an `exit`
 *      affordance that clears storage and strips the param via
 *      `history.replaceState`;
 *   6. it is NOT A SECURITY BOUNDARY — everything here is behind auth and
 *      firm-wide-visible by product principle. It reveals an animation.
 *
 * ON BY DEFAULT since 2026-09-14. To turn it off, append `?gamification=off`
 * to any app URL; the choice persists for the tab (sessionStorage) until
 * `?gamification=on` or a new tab. The celebration still renders only if Thrive
 * comes back `cleared` — the default decides whether it is ALLOWED to play, not
 * whether there is anything to celebrate.
 */
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

/** The URL param. Short, because it is now an OPT-OUT people type by hand
 *  rather than a demo link they are sent: `?gamification=off`. */
export const COMPARE_GAMIFICATION_PARAM = "gamification";

/** The param this flag shipped under, kept working. It was an opt-IN
 *  (`?gamification=off`), so every link carrying it asked for the mode
 *  that is now the default and keeps getting it either way — but honouring it
 *  costs three lines and means no shared link silently changes meaning.
 *  `COMPARE_GAMIFICATION_PARAM` wins when both are present. */
export const COMPARE_GAMIFICATION_PARAM_LEGACY = "compare-gamification";

/** sessionStorage key. Namespaced `nu_` like `nu_preview_tier`, and distinct
 *  from it: the two modes are independent and one must never re-key the other. */
export const COMPARE_GAMIFICATION_STORAGE_KEY = "nu_compare_gamification";

/** `"on"` reveals the celebration and IS THE DEFAULT (2026-09-14); `"off"` is
 *  the explicit opt-out, and the only one of the two anybody needs to type. */
export type CompareGamificationMode = "on" | "off";

interface CompareGamificationContextValue {
  /** The active mode. */
  gamificationMode: CompareGamificationMode;
  /** Derived: `gamificationMode === "on"`. What components actually read. */
  celebrationsEnabled: boolean;
  /** Drop the persisted mode + the URL param. */
  exitGamification: () => void;
}

/** The value read OUTSIDE the provider.
 *
 *  STAYS OFF, even though the provider now defaults ON, and the two are not in
 *  conflict: "no provider" is not a recruiter who did not opt out, it is a
 *  surface with no gamification wiring at all (an isolated component render,
 *  another package). Defaulting it on would start celebrating in places that
 *  never mounted the feature. `main.tsx` mounts the provider, so the app —
 *  every surface this flag is about — gets the ON default. */
const OFF: CompareGamificationContextValue = {
  gamificationMode: "off",
  celebrationsEnabled: false,
  exitGamification: () => {},
};

const CompareGamificationContext = createContext<CompareGamificationContextValue | null>(null);

/** Exact-string, case-sensitive. `"ON"`, `"true"`, `"1"` and everything else
 *  are UNRECOGNISED, which is not the same as `"off"` — see `readInitialMode`. */
function parseMode(raw: string | null): CompareGamificationMode | null {
  if (raw === "on") return "on";
  if (raw === "off") return "off";
  return null;
}

function readPersistedMode(): CompareGamificationMode {
  try {
    return parseMode(sessionStorage.getItem(COMPARE_GAMIFICATION_STORAGE_KEY)) ?? "on";
  } catch {
    // `sessionStorage` throws in a private window or with site data blocked.
    // The flag is never worth breaking the app for, and since the mode became
    // default-ON the safe failure is the default: a recruiter who cannot
    // persist an opt-out still gets the product everybody else sees, and can
    // re-add `?gamification=off` on the URL for the tab they are in.
    return "on";
  }
}

function readInitialMode(): CompareGamificationMode {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return readPersistedMode();
  }
  // FIRST value on a repeated param — the platform default. The current name
  // wins over the legacy one so a link carrying both is not ambiguous.
  const raw =
    params.get(COMPARE_GAMIFICATION_PARAM) ?? params.get(COMPARE_GAMIFICATION_PARAM_LEGACY);
  if (raw === null) return readPersistedMode();
  const parsed = parseMode(raw);
  if (parsed === null) {
    // Unrecognised. Treated as ABSENT — do NOT touch sessionStorage, so a typo
    // in a re-shared link cannot clear the mode a returning tab already had.
    return readPersistedMode();
  }
  try {
    // MIRRORED when the default flipped: the STORED value is now the opt-out,
    // and "on" is expressed by the absence of a key. Storing "on" instead would
    // still work today and would quietly pin a tab to on if the default ever
    // moves again — the key means "this person asked for something other than
    // the default", which is the thing worth persisting.
    if (parsed === "on") sessionStorage.removeItem(COMPARE_GAMIFICATION_STORAGE_KEY);
    else sessionStorage.setItem(COMPARE_GAMIFICATION_STORAGE_KEY, parsed);
  } catch {
    // Same as above: the mode still applies for this render, it just will not
    // survive a navigation.
  }
  return parsed;
}

export function CompareGamificationProvider({ children }: { children: ReactNode }) {
  const [gamificationMode, setMode] = useState<CompareGamificationMode>(readInitialMode);

  // INVERTED WITH THE DEFAULT (2026-09-14). This used to turn the mode off by
  // CLEARING the persisted value and the URL param — correct while "nothing
  // stored" meant off, and exactly backwards now that it means on: the tab went
  // quiet for one render and came back celebrating on the next reload. Turning
  // it off is now something to RECORD, in both places.
  const exitGamification = useCallback(() => {
    try {
      sessionStorage.setItem(COMPARE_GAMIFICATION_STORAGE_KEY, "off");
    } catch {
      // Nothing to do — the in-memory mode below is what this render reads, and
      // the URL below carries the choice across a reload on its own.
    }
    const url = new URL(window.location.href);
    url.searchParams.set(COMPARE_GAMIFICATION_PARAM, "off");
    // The legacy opt-in would otherwise sit beside the new opt-out in the bar,
    // reading as a contradiction even though the current name wins.
    url.searchParams.delete(COMPARE_GAMIFICATION_PARAM_LEGACY);
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    setMode("off");
  }, []);

  const value = useMemo<CompareGamificationContextValue>(
    () => ({
      gamificationMode,
      celebrationsEnabled: gamificationMode === "on",
      exitGamification,
    }),
    [gamificationMode, exitGamification]
  );

  return (
    <CompareGamificationContext.Provider value={value}>
      {children}
    </CompareGamificationContext.Provider>
  );
}

/**
 * TOLERANT OF A MISSING PROVIDER, unlike `usePreview`, and the difference is
 * deliberate: this flag gates a decoration on a modal that several isolated
 * suites (and any future surface) render without the app shell. `usePreview`
 * throws because a missing preview context means a page is rendering the wrong
 * feature set; a missing gamification context can only ever mean "no confetti",
 * which is the default anyway. A demo flag must not be able to crash the modal
 * it decorates.
 */
export function useCompareGamification(): CompareGamificationContextValue {
  return useContext(CompareGamificationContext) ?? OFF;
}
